import round from "lodash/round";
import { Objective, KeyResult } from "@/types";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { OkrProgressBar } from "./OkrProgressBar";
import { KeyResultList } from "./KeyResultList";
import { OkrStatusBadge } from "./OkrStatusBadge";
import { KrWeightSummary } from "./KrWeightSummary";
import { UserAvatar } from "@/components/UserAvatar";
import { Pencil, Plus, Trash2, Calendar, Clock } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { MarkdownRenderer } from "@/components/ui/MarkdownRenderer";
import { useSetOpenEntity } from "@/components/copilot/context/CopilotEntityContext";
import { ignoreCopilotInteractOutside } from "@/components/copilot/context/copilotOutsideInteraction";
import { useKeyResults, krListParams } from "@/hooks/useKeyResults";
import { useObjective } from "@/hooks/useObjectives";

interface ObjectiveDetailSheetProps {
  objective: Objective | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (objective: Objective) => void;
  onDelete: (objective: Objective) => void;
  onAddKeyResult: (objective: Objective) => void;
  onEditKeyResult: (keyResult: KeyResult) => void;
  onDeleteKeyResult: (keyResult: KeyResult) => void;
  /** OKR writes are C-level only */
  canManage?: boolean;
}

export function ObjectiveDetailSheet({
  objective: objectiveProp,
  open,
  onOpenChange,
  onEdit,
  onDelete,
  onAddKeyResult,
  onEditKeyResult,
  onDeleteKeyResult,
  canManage = false,
}: ObjectiveDetailSheetProps) {
  // The prop is a snapshot taken when the sheet opened; follow the query so inline KR saves show up.
  const { data: liveObjective } = useObjective(objectiveProp?.pid ?? "");
  const objective = liveObjective ?? objectiveProp;
  // Must stay above any conditional return — rules of hooks.
  useSetOpenEntity(
    open && objective
      ? {
          description: "Open objective detail",
          value: `Objective ${objective.code} — ${objective.name} (${objective.status})`,
        }
      : null,
  );
  const { data: krsData } = useKeyResults(krListParams(objective?.pid ?? ""), !!objective);
  const keyResults = krsData?.data ?? [];

  if (!objective) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        onInteractOutside={ignoreCopilotInteractOutside}
        className="sm:max-w-lg overflow-y-auto"
      >
        <SheetHeader className="pb-4">
          {/* Code + Status badges */}
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">
              {objective.code}
            </span>
            <OkrStatusBadge status={objective.status} size="md" />
          </div>
          <SheetTitle className="text-xl leading-tight">
            {objective.name}
          </SheetTitle>
          {objective.description && (
            <SheetDescription className="text-sm">
              {objective.description}
            </SheetDescription>
          )}
        </SheetHeader>

        {/* Meta: Owner + Dates */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
          {objective.owner ? (
            <div className="flex items-center gap-2">
              <UserAvatar
                src={objective.owner.avatarUrl}
                name={objective.owner.fullName}
                className="h-6 w-6"
              />
              <span>{objective.owner.fullName}</span>
            </div>
          ) : (
            <span>Unassigned</span>
          )}
          {objective.dueDate && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              <span>Due {new Date(objective.dueDate).toLocaleDateString()}</span>
            </div>
          )}
          {objective.createdAt && (
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span>Created {new Date(objective.createdAt).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Strategy: markdown rationale (why this goal, how the team intends to win) — stored as `context` */}
        {objective.context && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold mb-2">Strategy</h4>
            <div className="rounded-md border bg-muted/30 p-3">
              <MarkdownRenderer content={objective.context} />
            </div>
          </div>
        )}

        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">Progress <span className="font-normal text-muted-foreground">· weighted from Key Results</span></span>
            <span className="text-sm font-medium tabular-nums">
              {round(objective.progress, 0)}/{round(objective.targetProgress, 0)}
            </span>
          </div>
          <OkrProgressBar
            progress={objective.progress}
            targetProgress={objective.targetProgress}
            showLabel={false}
          />
          <KrWeightSummary keyResults={keyResults} />
        </div>

        {/* Actions */}
        {canManage && <div className="flex items-center gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => {
              onEdit(objective);
              onOpenChange(false);
            }}
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => {
              onAddKeyResult(objective);
            }}
          >
            <Plus className="h-3.5 w-3.5" /> Add Key Result
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-destructive hover:text-destructive"
            onClick={() => {
              onDelete(objective);
              onOpenChange(false);
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        </div>}

        <Separator className="mb-4" />

        {/* Key Results */}
        <div>
          <h4 className="text-sm font-semibold mb-3">Key Results</h4>
          <KeyResultList
            objectivePid={objective.pid}
            onEditKeyResult={onEditKeyResult}
            onDeleteKeyResult={onDeleteKeyResult}
            compact
            canManage={canManage}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
